/**
 * InstallGuideModal — panel ITE „Serwery baz tenantów": instrukcja wdrożenia bazy tenanta na
 * serwerze klienta (ADR-0113b) + pobranie skryptu `install-client-db-server.sh` + gotowe polecenie
 * z adresem naszego serwera aplikacji (`DB_SERVERS_APP_IP` po stronie API).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import {
  downloadInstallScript,
  INSTALL_SCRIPT_FILE,
  useInstallScriptInfo,
} from '@/lib/db-servers/api';

interface Props {
  open: boolean;
  onClose: () => void;
}

const STEPS = ['agree', 'tenant', 'install', 'server', 'prepare', 'provision', 'later'] as const;

export function InstallGuideModal({ open, onClose }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const info = useInstallScriptInfo(open);
  const [downloading, setDownloading] = useState(false);

  const appIp = info.data?.appIps.length
    ? info.data.appIps.join(',')
    : t('admin.dbServers.guide.appIpPlaceholder');
  const command = `sudo bash ${INSTALL_SCRIPT_FILE} --app-ip ${appIp} --hostname ${t(
    'admin.dbServers.guide.hostPlaceholder',
  )}`;

  async function handleDownload() {
    setDownloading(true);
    try {
      await downloadInstallScript();
    } catch {
      toast.show({ variant: 'error', title: t('admin.dbServers.guide.downloadError') });
    } finally {
      setDownloading(false);
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(command);
      toast.show({ variant: 'success', title: t('admin.dbServers.guide.copied') });
    } catch {
      toast.show({ variant: 'error', title: t('admin.dbServers.guide.copyError') });
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin.dbServers.guide.title')}
      description={t('admin.dbServers.guide.description')}
      size="lg"
      footer={
        <Button variant="primary" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      <div className="flex flex-col gap-5 text-sm text-text-primary" data-testid="db-servers-guide">
        <ol className="flex list-decimal flex-col gap-3 pl-5">
          {STEPS.map((step) => (
            <li key={step}>
              <p className="font-medium">{t(`admin.dbServers.guide.steps.${step}.title`)}</p>
              <p className="mt-0.5 text-text-secondary">
                {t(`admin.dbServers.guide.steps.${step}.body`)}
              </p>
            </li>
          ))}
        </ol>

        <div className="flex flex-col gap-2 rounded-lg border border-border-primary p-4">
          <p className="font-medium">{t('admin.dbServers.guide.scriptTitle')}</p>
          <p className="text-text-secondary">{t('admin.dbServers.guide.scriptBody')}</p>
          <div>
            <Button
              variant="secondary"
              loading={downloading}
              onClick={() => void handleDownload()}
              data-testid="db-servers-guide-download"
            >
              {t('admin.dbServers.guide.download')}
            </Button>
          </div>
          {info.data?.sha256 && (
            <p className="break-all text-xs text-text-tertiary" data-testid="db-servers-guide-sha">
              {t('admin.dbServers.guide.checksum', { sha256: info.data.sha256 })}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <p className="font-medium">{t('admin.dbServers.guide.commandTitle')}</p>
          <pre
            className="overflow-x-auto whitespace-pre-wrap break-all rounded-lg bg-bg-tertiary p-3 font-mono text-xs"
            data-testid="db-servers-guide-command"
          >
            {command}
          </pre>
          {info.isError && (
            <p role="alert" className="text-xs text-red-700 dark:text-red-300">
              {t('admin.dbServers.guide.infoError')}
            </p>
          )}
          {info.data && info.data.appIps.length === 0 && (
            <p className="text-xs text-text-tertiary">{t('admin.dbServers.guide.noAppIp')}</p>
          )}
          <p className="text-xs text-text-tertiary">{t('admin.dbServers.guide.commandHint')}</p>
          <div>
            <Button variant="secondary" size="sm" onClick={() => void handleCopy()}>
              {t('admin.dbServers.guide.copy')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
